import {
    Dialog,
    domain,
    useDeviceSize,
    useNotifications,
    convertDateToMobile,
} from "@relode/core";
import { Formik } from "formik";
import * as React from "react";
import * as Yup from "yup";
import { HiredCandidateForm } from "./hiredCandidateForm";
import { HiredCandidateDialogProps } from "./hiredCandidateDialogProps";

export const HiredCandidateDialog = React.memo(
    (props: HiredCandidateDialogProps) => {
        const {
            candidate,
            candidateName,
            job,
            onClose,
            onComplete,
            onSubmit,
        } = props;
        const deviceSize = useDeviceSize();
        const { actions: notifications } = useNotifications();
        const isContractOrLocum = job.jobType.isContractOrLocum();

        const handleOnClose = () => {
            notifications.closeAreaNotification("hireErrorArea");
            onClose();
        };

        const validationSchema = () =>
            Yup.object().shape(
                {
                    hiredJobSalaryInUSDollars: Yup.number()
                        .when(["hiredJobSalaryTypeId"], {
                            is: val =>
                                +val === domain.JobSalaryTypeEnum.PerYear,
                            then: Yup.number()
                                .min(
                                    10000,
                                    "please enter an amount more than $10,000.00"
                                )
                                .max(
                                    1000000,
                                    "please enter an amount less than $1,000,000.00"
                                ),
                        })
                        .when(["hiredJobSalaryTypeId"], {
                            is: val =>
                                +val === domain.JobSalaryTypeEnum.PerHour,
                            then: Yup.number()
                                .min(
                                    10,
                                    "please enter an amount more than $10.00"
                                )
                                .max(
                                    1000,
                                    "please enter an amount less than $1,000.00"
                                ),
                        })
                        .required("please enter a starting amount")
                        .nullable(true),
                    hiredJobSalaryTypeId: Yup.string()
                        .required("pay type is required")
                        .nullable(true),
                    hiredStartDate: Yup.date()
                        .required("please enter a start date")
                        .typeError("please enter a start date")
                        .nullable(true)
                        .when(["hiredEndDate"], {
                            is: end =>
                                !!end &&
                                isContractOrLocum &&
                                end?.toString() !== "Invalid Date",
                            then: Yup.date()
                                .required("please enter a start date")
                                .typeError("please enter a start date")
                                .nullable(true)
                                .max(
                                    Yup.ref("hiredEndDate"),
                                    "start date must be before end date"
                                ),
                        }),
                    hiredEndDate: Yup.date()
                        .when(["hiredStartDate"], {
                            is: start => !!start && isContractOrLocum,
                            then: Yup.date()
                                .min(
                                    Yup.ref("hiredStartDate"),
                                    "end date must be after start date"
                                )
                                .required("please enter an end date")
                                .typeError("please enter an end date")
                                .nullable(true),
                        })
                        .typeError("please enter an end date")
                        .nullable(true),
                    hiredHoursPerWeek: Yup.number()
                        .required("please enter hours per week")
                        .test({
                            name: "hoursPerWeekTest",
                            message: "invalid hours per week",
                            test: function (value: number) {
                                if (isContractOrLocum) {
                                    if (value < 1 || value > 168) {
                                        return this.createError({
                                            path: "hiredHoursPerWeek",
                                            message:
                                                "hours per week must be between 1 and 168",
                                        });
                                    }
                                }
                                return true;
                            },
                        })
                        .nullable(false),
                },
                [
                    ["hiredStartDate", "hiredEndDate"],
                    ["hiredEndDate", "hiredEndDate"],
                    ["hiredHoursPerWeek", "hiredHoursPerWeek"],
                    ["hiredStartDate", "hiredStartDate"],
                ]
            );

        const _candidate = { ...candidate };
        if (deviceSize === "mobile") {
            _candidate.hiredStartDate = convertDateToMobile<any>(
                candidate.hiredStartDate
            );
            _candidate.hiredEndDate = convertDateToMobile<any>(
                candidate.hiredEndDate
            );
        }

        return (
            <Dialog
                open
                onClose={handleOnClose}
                //classes={{ root: classes.mui_dialog_root }}
                //fullWidth={device == "desktop" ? true : false}
                //maxWidth={device == "desktop" ? "sm" : undefined}
                //fullScreen={device === "mobile"}
            >
                <Formik
                    name="hiredCandidateForm"
                    initialValues={_candidate}
                    onSubmit={() => {
                        /* noop */
                    }}
                    validateOnBlur={false}
                    validateOnChange={true}
                    validationSchema={validationSchema}
                >
                    {() => (
                        <HiredCandidateForm
                            onClose={handleOnClose}
                            onComplete={onComplete}
                            onSubmit={onSubmit}
                            job={job}
                            candidateName={candidateName}
                            showUserAttributions={false}
                        />
                    )}
                </Formik>
                {}
            </Dialog>
        );
    }
);

HiredCandidateDialog.displayName = "HiredCandidateModal";
